import { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@immo/ui-kit';
import { MIN_SEARCH_QUERY_LENGTH, type Permission, type SearchEntityType } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { useWorkspaceRoutes } from '@/lib/workspace-path';
import { useCommandPalette } from './command-palette-provider';
import { GlobalSearchEntry } from './global-search-entry';
import type { PaletteItem } from './palette-item';
import { PaletteFooter } from './palette-footer';
import { QuickNav } from './quick-nav';
import { visibleQuickNavEntries } from './quick-nav-items';
import { allResultsTarget, resultTarget } from './result-target';
import { SearchResults } from './search-results';
import { useGlobalSearch } from './use-global-search';

type Props = {
  readonly workspaceId: string;
  readonly permissions: ReadonlyArray<Permission>;
};

/**
 * The command palette overlay. Composition over `@immo/ui-kit`'s `Modal`, which
 * owns the portal, the scroll lock, the focus trap and the focus restore — the
 * palette only supplies the frame, the content and the keyboard model.
 *
 * Both views reduce to one flat list of `PaletteItem`s, so ↑/↓ cross group
 * boundaries and Enter, a click and the highlight can never point at different
 * rows.
 */
export function CommandPalette({ workspaceId, permissions }: Props) {
  const { open, query, restoreFocusOnClose, setQuery, close } = useCommandPalette();
  const t = useTranslations('pages.shell.search');
  const tGroup = useTranslations('pages.shell.search.groups');
  const tNav = useTranslations('pages.shell.nav');
  const navigate = useNavigate();
  const routes = useWorkspaceRoutes();
  const [activeIndex, setActiveIndex] = useState(0);
  const listboxId = useId();

  const searching = query.trim().length >= MIN_SEARCH_QUERY_LENGTH;
  const { groups, loading, failed, settledQuery } = useGlobalSearch(workspaceId, query, open);
  const quickNavEntries = visibleQuickNavEntries(permissions);

  // The „Alle Treffer in {Bereich}" row for one group, as a zero-or-one list so
  // it can be spread straight into the flat item list. Empty when the group is
  // not truncated OR when the entity has no list to open — the row is a
  // destination, and a destination-less row would highlight and do nothing.
  const allResultsItems = (entityType: SearchEntityType, truncated: boolean): ReadonlyArray<PaletteItem> => {
    if (!truncated) return [];

    const to = allResultsTarget(routes, entityType, settledQuery);

    if (to === null) return [];

    return [
      {
        key: `${entityType}-all-results`,
        optionId: `${listboxId}-${entityType}-all-results`,
        groupKey: entityType,
        kind: 'all-results',
        primary: t('all-results', { area: tGroup(entityType) }),
        secondary: null,
        secondaryToken: null,
        to,
      },
    ];
  };

  // Deliberately NOT memoised. The list is at most a handful of rows, and the
  // dependency set is a trap: `useWorkspaceRoutes()` is memoised on the
  // workspace id, so a `useMemo` that omits it keeps serving targets for the
  // workspace the member just switched away from.
  const items: ReadonlyArray<PaletteItem> = searching
    ? groups.flatMap((group) => [
        ...group.rows.map((row) => ({
          key: `${group.entityType}-${row.id}`,
          optionId: `${listboxId}-${group.entityType}-${row.id}`,
          groupKey: group.entityType,
          kind: 'result' as const,
          primary: row.primary,
          secondary: row.secondary,
          secondaryToken: row.secondaryToken,
          to: resultTarget(routes, group.entityType, row),
        })),
        // The truncated group's destination row (IMM-448.4). Appended to the
        // SAME flat list rather than rendered beside it, so the keyboard model
        // needs no special case: it is walked, highlighted and opened like any
        // other row. Conditional on a list destination existing, not merely on
        // the group being truncated — Dokumente has none.
        ...allResultsItems(group.entityType, group.total > group.rows.length),
      ])
    : quickNavEntries.map((entry) => ({
        key: entry.key,
        optionId: `${listboxId}-${entry.key}`,
        groupKey: 'quick-nav',
        kind: 'result' as const,
        primary: tNav(entry.labelKey),
        secondary: null,
        secondaryToken: null,
        to: entry.to(routes),
      }));

  // The list scrolls but focus never leaves the field, so the browser will not
  // follow the highlight on its own — without this, ↑/↓ walks the selection off
  // the bottom of the list and the member sees nothing move.
  useEffect(() => {
    const active = items[activeIndex];

    if (active === undefined) return;

    // Optional call: jsdom has no layout and so no `scrollIntoView`.
    document.getElementById(active.optionId)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, items]);

  // A fresh open starts at the top, and so does every new result set — a
  // highlight held over from the previous query would point at a row the member
  // never saw.
  useEffect(() => {
    setActiveIndex(0);
  }, [open, settledQuery, searching]);

  const select = (item: PaletteItem) => {
    if (item.to === null) return;

    // Close here rather than leaving it to the route-change effect: navigating
    // to the page the member is already on changes only the location key, so
    // that effect would never fire and the overlay would stay up over the page
    // it was asked to show. Focus is not restored — the element that had it
    // leaves with the old page.
    close({ restoreFocus: false });
    navigate(item.to);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (items.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % items.length);

      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + items.length) % items.length);

      return;
    }

    if (event.key !== 'Enter') return;

    const item = items[activeIndex];

    if (item === undefined) return;

    event.preventDefault();
    select(item);
  };

  const active = items[activeIndex];
  // Guarded on the settled query, not just on emptiness: on the keystroke that
  // reaches the two-character floor the hook has not answered yet, and an
  // unguarded check paints „Keine Treffer für „"." with an empty interpolation.
  const settled = settledQuery === query.trim();
  const noHits = searching && settled && !loading && !failed && items.length === 0;

  return (
    <Modal
      open={open}
      onClose={close}
      size="lg"
      hideHeader
      ariaLabel={t('placeholder')}
      dismissOnBackdrop
      confirmOnClose={false}
      restoreFocus={restoreFocusOnClose}
      backdropClassName="items-center"
      className="max-w-[640px] overflow-hidden rounded-lg shadow-xl"
      bodyClassName="p-0"
    >
      <div onKeyDown={onKeyDown} className="flex flex-col">
        <GlobalSearchEntry
          variant="overlay"
          value={query}
          onValueChange={setQuery}
          controlsId={listboxId}
          activeDescendantId={active === undefined ? undefined : active.optionId}
        />
        {!searching && (
          <QuickNav
            listboxId={listboxId}
            items={items}
            activeIndex={activeIndex}
            onHighlight={setActiveIndex}
            onSelect={select}
          />
        )}
        {searching && failed && (
          <p role="alert" className="px-4 py-6 text-sm text-text-tertiary">
            {t('failed')}
          </p>
        )}
        {searching && !failed && noHits && (
          <p className="px-4 py-6 text-sm text-text-tertiary">{t('no-hits', { query: settledQuery })}</p>
        )}
        {searching && !failed && !noHits && (
          <SearchResults
            listboxId={listboxId}
            groups={groups}
            items={items}
            activeIndex={activeIndex}
            onHighlight={setActiveIndex}
            onSelect={select}
          />
        )}
        <PaletteFooter />
      </div>
    </Modal>
  );
}
